import { FC, useMemo } from 'react';
import { Colors, FInter, FlexBox, Fonts } from '@brg/ui';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LayoutConst } from "../modules";
import { TranslatedIcon } from 'shared/icons/TranslatedIcon';
import {
  getProjectLangSwitcherActiveCode,
  getProjectLangSwitcherBadge,
  getProjectLangSwitcherPrimaryCode,
  getProjectLangSwitcherPrimaryLabel,
  getProjectLangSwitcherSecondaryLabel,
} from 'shared/helpers/projectLangSwitcher';

const LanguageSwitchContainer = styled(FlexBox)<{ visible?: boolean, isMobile?: boolean }>`
  position: relative;
  width: max-content;
`

const LanguageItem = styled.div<{ active?: boolean, color?: string }>`
  ${Fonts.Inter.text};
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700 !important;
  font-size: 13px !important;
  line-height: 20px !important;
  padding: 6px 8px;
  border-radius: 3px;
  background: ${({ theme, active }) => active ? theme.navigationBar.btns.changeLang.background : 'transparent'};
  box-shadow: 0 ${({ active }) => active ? '4px 4px' : '0 0'} rgba(0, 0, 0, 0.12);
  transition: box-shadow 300ms, background 300ms, color 300ms;
  color: ${({
    active,
    theme,
  }) => active ? theme.navigationBar.btns.changeLang.active : theme.navigationBar.btns.changeLang.color};
  cursor: pointer;

  @media(max-width: ${LayoutConst.screen.l_16}px) {
    font-size: 10px !important;
    line-height: 18px !important;
    padding: 5px 7px;
  }
  
  :not(:last-child) {
    margin-right: 4px;
  }
`
const LanguageItemMobile = styled.div<{ active?: boolean, color?: string }>`
  ${Fonts.Inter.text};
  font-weight: 700 !important;
  font-size: 13px !important;
  line-height: 20px !important;
  padding: 6px 8px;
  border-radius: 3px;
  background-color: transparent;
  color: ${({ theme, active }) => active ? theme.navigationBar.btns.changeLang.colorMobile : theme.navigationBar.btns.changeLang.color};

  transition: box-shadow 300ms, background 300ms, color 300ms;
  cursor: pointer;

  :last-child {
    margin-right: 0;
    padding-right: 0;
  }
  // :not(:last-child) {
  //   margin-right: 4px;
  // }
`;

const SwitcherItem = styled(FlexBox)<{ isActive?: boolean, isCompetitionEdit?: boolean }>`
  font-family: 'Inter';
  width: 100%;
  height: 36px;
  place-items: center;
  justify-content: center;
  background-color: transparent;
  color: ${({ theme, isCompetitionEdit }) => isCompetitionEdit ? theme?.competition.langComponent.mainSwitcher.notActive.color : theme?.projectEdit.btns.lang.disabledColor};
  cursor: pointer;
  border-radius: 3px;
  font-weight: 600;
  letter-spacing: -0.02em;
  font-size: 17px;
  line-height: 22px;
  
  ${({ isActive, isCompetitionEdit }) => isActive ? css`
    background-color: ${({ theme }) => isCompetitionEdit ? theme?.competition.langComponent.mainSwitcher.active.bg : theme?.projectEdit.btns.lang.active};
    color: ${({ theme }) => isCompetitionEdit ? theme?.competition.langComponent.mainSwitcher.active.color : theme?.projectEdit.btns.lang.activeColor};
  `: null};

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    ${({ isCompetitionEdit }) => isCompetitionEdit && css`
      font-size: 15px;
      line-height: 20px;
      font-weight: 400;
    `};
  }

  @media(max-width: ${LayoutConst.screen.m}px) {
    font-size: 13px;
    line-height: 16px;
  }
`;

const RadioBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-direction: row;
`

export const RadioCircle = styled(FlexBox)<{ disabled?: boolean }>`
  width: 24px;
  height: 24px;
  // border: 1px solid ${Colors.galBlack21};
  box-sizing: border-box;
  border-radius: 12px;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  svg {
    opacity: 0;
    transition: all 0.2s;
  }
  background: #E7E7E7;

  &:after {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: transparent;
  }
`;

const RadioLabel = styled(FInter.SmallTextBold)<{ clr?: string }>`
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 16px;
  color: ${({ theme }) => theme.projectEdit.label};
  @media(min-width: 1920px) {
    font-size: 13px;
    line-height: 16px;
  }
`

export const RadioInputSource = styled.input`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  height: 100%;
  opacity: 0;

  &:checked {
    & ~ ${RadioBox} {

      ${RadioCircle} {
        border: 1px solid #B6B6B5;
        :after {
          content: "";
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #B6B6B5;
        }
      }
    }
  }
`;

const RadioContainer = styled(FlexBox)`
    position: relative;
    width: max-content;
`;

const SwitcherContainer = styled(FlexBox)<{ fs?: boolean }>`
    position: relative;
    width: 100%;
    max-width: ${({ fs }) => fs ? '100%' : '400px'};
    border: 1px solid #B6B6B5;
    border-radius: 3px;

    @media(max-width: ${LayoutConst.screen.md}px) {
      max-width: 100%;
      min-width: 204px;
    }
`;


export const BLanguageSelectionComponent: FC<{ onLangSelect?: (lang: string) => void, mobile?: boolean, isProjectPage?: boolean, isCompetitionEdit?: boolean, fs?: boolean }> = ({ onLangSelect, mobile = false, isProjectPage = false, isCompetitionEdit = false, fs = false }) => {
  const { t, i18n } = useTranslation();
  const primaryLangCode = getProjectLangSwitcherPrimaryCode();
  const activeLangCode = getProjectLangSwitcherActiveCode(i18n.language);
  const primaryLanguage = getProjectLangSwitcherBadge(primaryLangCode);
  const primaryLabel = getProjectLangSwitcherPrimaryLabel(t);
  const secondaryLabel = getProjectLangSwitcherSecondaryLabel(t);
  const isPrimaryActive = activeLangCode === primaryLangCode;
  const isEnglishActive = activeLangCode === 'en';

  const Radio = useMemo(() => (
    <RadioContainer direction='row' gx={16}>
    <FlexBox style={{ position: 'relative'}} direction='row' onClick={() => onLangSelect?.(primaryLangCode)}>
      <RadioInputSource type={'radio'} checked={isPrimaryActive} />
        <RadioBox >
          <RadioCircle disabled={isEnglishActive} />
          <RadioLabel  >
            {primaryLabel}
          </RadioLabel>
        </RadioBox>
    </FlexBox>
    <FlexBox style={{ position: 'relative'}} direction='row' onClick={() => onLangSelect?.('en')}>
      <RadioInputSource type={'radio'} checked={isEnglishActive} />
        <RadioBox >
          <RadioCircle disabled={isPrimaryActive} />
          <RadioLabel  >
          {secondaryLabel}
          </RadioLabel>
        </RadioBox>
    </FlexBox>
  </RadioContainer>
  ), [isPrimaryActive, isEnglishActive, onLangSelect, primaryLabel, secondaryLabel, primaryLangCode])

  const Switcher = useMemo(() => (
    <SwitcherContainer direction='row' width={'100%'} widthMobile={'100%'} fs={fs} className='switcher-container'>
      <FlexBox width={'100%'} widthMobile={'100%'} style={{ position: 'relative'}} direction='row' onClick={() => onLangSelect?.(primaryLangCode)}>
        <SwitcherItem isCompetitionEdit={isCompetitionEdit} isActive={isPrimaryActive}>{primaryLabel}</SwitcherItem>
      </FlexBox>
      <FlexBox width={'100%'} widthMobile={'100%'} style={{ position: 'relative' }} direction='row' onClick={() => onLangSelect?.('en')}>
        <SwitcherItem isCompetitionEdit={isCompetitionEdit} isActive={isEnglishActive}>{secondaryLabel} 
          <span style={{marginLeft: '4px', marginTop: '-4px'}}>
            {/* <TranslatedIcon isActive={isEnglishActive}/> */}
          </span>
        </SwitcherItem>
      </FlexBox>
  </SwitcherContainer>
  ), [isPrimaryActive, isEnglishActive, onLangSelect, primaryLabel, secondaryLabel, isCompetitionEdit, fs, primaryLangCode])

  if (isProjectPage || isCompetitionEdit) {
    return (
      <>
        {Switcher}
      </>
    )
  }

  return <LanguageSwitchContainer>
    {mobile ? (
      <>
        <LanguageItemMobile
          active={isPrimaryActive}
          onClick={() => onLangSelect?.(primaryLangCode)}
        >
          {primaryLanguage}
        </LanguageItemMobile>
        <LanguageItemMobile
          active={isEnglishActive}
          onClick={() => onLangSelect?.('en')}
        >
          EN 
        </LanguageItemMobile>
      </>
    ) : (
      <>
        <LanguageItem
          active={isPrimaryActive}
          onClick={() => onLangSelect?.(primaryLangCode)}
        >
          {primaryLanguage}
        </LanguageItem>
        <LanguageItem
          active={isEnglishActive}
          onClick={() => onLangSelect?.('en')}
        >
          EN
        </LanguageItem>
      </>
    )}

  </LanguageSwitchContainer>
};
import { FC, useMemo } from 'react';
import { Colors, FInter, FlexBox, Fonts } from '@brg/ui';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LayoutConst } from "../modules";
import { TranslatedIcon } from 'shared/icons/TranslatedIcon';
import {
  getProjectLangSwitcherActiveCode,
  getProjectLangSwitcherBadge,
  getProjectLangSwitcherPrimaryCode,
  getProjectLangSwitcherPrimaryLabel,
  getProjectLangSwitcherSecondaryLabel,
} from 'shared/helpers/projectLangSwitcher';

const LanguageSwitchContainer = styled(FlexBox)<{ visible?: boolean, isMobile?: boolean }>`
  position: relative;
  width: max-content;
`

const LanguageItem = styled.div<{ active?: boolean, color?: string }>`
  ${Fonts.Inter.text};
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700 !important;
  font-size: 13px !important;
  line-height: 20px !important;
  padding: 6px 8px;
  border-radius: 3px;
  background: ${({ theme, active }) => active ? theme.navigationBar.btns.changeLang.background : 'transparent'};
  box-shadow: 0 ${({ active }) => active ? '4px 4px' : '0 0'} rgba(0, 0, 0, 0.12);
  transition: box-shadow 300ms, background 300ms, color 300ms;
  color: ${({
    active,
    theme,
  }) => active ? theme.navigationBar.btns.changeLang.active : theme.navigationBar.btns.changeLang.color};
  cursor: pointer;

  @media(max-width: ${LayoutConst.screen.l_16}px) {
    font-size: 10px !important;
    line-height: 18px !important;
    padding: 5px 7px;
  }
  
  :not(:last-child) {
    margin-right: 4px;
  }
`
const LanguageItemMobile = styled.div<{ active?: boolean, color?: string }>`
  ${Fonts.Inter.text};
  font-weight: 700 !important;
  font-size: 13px !important;
  line-height: 20px !important;
  padding: 6px 8px;
  border-radius: 3px;
  background-color: transparent;
  color: ${({ theme, active }) => active ? theme.navigationBar.btns.changeLang.colorMobile : theme.navigationBar.btns.changeLang.color};

  transition: box-shadow 300ms, background 300ms, color 300ms;
  cursor: pointer;

  :last-child {
    margin-right: 0;
    padding-right: 0;
  }
  // :not(:last-child) {
  //   margin-right: 4px;
  // }
`;

const SwitcherItem = styled(FlexBox)<{ isActive?: boolean, isCompetitionEdit?: boolean }>`
  font-family: 'Inter';
  width: 100%;
  height: 36px;
  place-items: center;
  justify-content: center;
  background-color: transparent;
  color: ${({ theme, isCompetitionEdit }) => isCompetitionEdit ? theme?.competition.langComponent.mainSwitcher.notActive.color : theme?.projectEdit.btns.lang.disabledColor};
  cursor: pointer;
  border-radius: 3px;
  font-weight: 600;
  letter-spacing: -0.02em;
  font-size: 17px;
  line-height: 22px;
  
  ${({ isActive, isCompetitionEdit }) => isActive ? css`
    background-color: ${({ theme }) => isCompetitionEdit ? theme?.competition.langComponent.mainSwitcher.active.bg : theme?.projectEdit.btns.lang.active};
    color: ${({ theme }) => isCompetitionEdit ? theme?.competition.langComponent.mainSwitcher.active.color : theme?.projectEdit.btns.lang.activeColor};
  `: null};

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    ${({ isCompetitionEdit }) => isCompetitionEdit && css`
      font-size: 15px;
      line-height: 20px;
      font-weight: 400;
    `};
  }

  @media(max-width: ${LayoutConst.screen.m}px) {
    font-size: 13px;
    line-height: 16px;
  }
`;

const RadioBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-direction: row;
`

export const RadioCircle = styled(FlexBox)<{ disabled?: boolean }>`
  width: 24px;
  height: 24px;
  // border: 1px solid ${Colors.galBlack21};
  box-sizing: border-box;
  border-radius: 12px;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  svg {
    opacity: 0;
    transition: all 0.2s;
  }
  background: #E7E7E7;

  &:after {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: transparent;
  }
`;

const RadioLabel = styled(FInter.SmallTextBold)<{ clr?: string }>`
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 16px;
  color: ${({ theme }) => theme.projectEdit.label};
  @media(min-width: 1920px) {
    font-size: 13px;
    line-height: 16px;
  }
`

export const RadioInputSource = styled.input`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  height: 100%;
  opacity: 0;

  &:checked {
    & ~ ${RadioBox} {

      ${RadioCircle} {
        border: 1px solid #B6B6B5;
        :after {
          content: "";
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #B6B6B5;
        }
      }
    }
  }
`;

const RadioContainer = styled(FlexBox)`
    position: relative;
    width: max-content;
`;

const SwitcherContainer = styled(FlexBox)<{ fs?: boolean }>`
    position: relative;
    width: 100%;
    max-width: ${({ fs }) => fs ? '100%' : '400px'};
    border: 1px solid #B6B6B5;
    border-radius: 3px;

    @media(max-width: ${LayoutConst.screen.md}px) {
      max-width: 100%;
      min-width: 204px;
    }
`;


export const BLanguageSelectionComponent: FC<{ onLangSelect?: (lang: string) => void, mobile?: boolean, isProjectPage?: boolean, isCompetitionEdit?: boolean, fs?: boolean }> = ({ onLangSelect, mobile = false, isProjectPage = false, isCompetitionEdit = false, fs = false }) => {
  const { t, i18n } = useTranslation();
  const primaryLangCode = getProjectLangSwitcherPrimaryCode();
  const activeLangCode = getProjectLangSwitcherActiveCode(i18n.language);
  const primaryLanguage = getProjectLangSwitcherBadge(primaryLangCode);
  const primaryLabel = getProjectLangSwitcherPrimaryLabel(t);
  const secondaryLabel = getProjectLangSwitcherSecondaryLabel(t);
  const isPrimaryActive = activeLangCode === primaryLangCode;
  const isEnglishActive = activeLangCode === 'en';

  const Radio = useMemo(() => (
    <RadioContainer direction='row' gx={16}>
    <FlexBox style={{ position: 'relative'}} direction='row' onClick={() => onLangSelect?.(primaryLangCode)}>
      <RadioInputSource type={'radio'} checked={isPrimaryActive} />
        <RadioBox >
          <RadioCircle disabled={isEnglishActive} />
          <RadioLabel  >
            {primaryLabel}
          </RadioLabel>
        </RadioBox>
    </FlexBox>
    <FlexBox style={{ position: 'relative'}} direction='row' onClick={() => onLangSelect?.('en')}>
      <RadioInputSource type={'radio'} checked={isEnglishActive} />
        <RadioBox >
          <RadioCircle disabled={isPrimaryActive} />
          <RadioLabel  >
          {secondaryLabel}
          </RadioLabel>
        </RadioBox>
    </FlexBox>
  </RadioContainer>
  ), [isPrimaryActive, isEnglishActive, onLangSelect, primaryLabel, secondaryLabel, primaryLangCode])

  const Switcher = useMemo(() => (
    <SwitcherContainer direction='row' width={'100%'} widthMobile={'100%'} fs={fs} className='switcher-container'>
      <FlexBox width={'100%'} widthMobile={'100%'} style={{ position: 'relative'}} direction='row' onClick={() => onLangSelect?.(primaryLangCode)}>
        <SwitcherItem isCompetitionEdit={isCompetitionEdit} isActive={isPrimaryActive}>{primaryLabel}</SwitcherItem>
      </FlexBox>
      <FlexBox width={'100%'} widthMobile={'100%'} style={{ position: 'relative' }} direction='row' onClick={() => onLangSelect?.('en')}>
        <SwitcherItem isCompetitionEdit={isCompetitionEdit} isActive={isEnglishActive}>{secondaryLabel} 
          <span style={{marginLeft: '4px', marginTop: '-4px'}}>
            {/* <TranslatedIcon isActive={isEnglishActive}/> */}
          </span>
        </SwitcherItem>
      </FlexBox>
  </SwitcherContainer>
  ), [isPrimaryActive, isEnglishActive, onLangSelect, primaryLabel, secondaryLabel, isCompetitionEdit, fs, primaryLangCode])

  if (isProjectPage || isCompetitionEdit) {
    return (
      <>
        {Switcher}
      </>
    )
  }

  return <LanguageSwitchContainer>
    {mobile ? (
      <>
        <LanguageItemMobile
          active={isPrimaryActive}
          onClick={() => onLangSelect?.(primaryLangCode)}
        >
          {primaryLanguage}
        </LanguageItemMobile>
        <LanguageItemMobile
          active={isEnglishActive}
          onClick={() => onLangSelect?.('en')}
        >
          EN 
        </LanguageItemMobile>
      </>
    ) : (
      <>
        <LanguageItem
          active={isPrimaryActive}
          onClick={() => onLangSelect?.(primaryLangCode)}
        >
          {primaryLanguage}
        </LanguageItem>
        <LanguageItem
          active={isEnglishActive}
          onClick={() => onLangSelect?.('en')}
        >
          EN
        </LanguageItem>
      </>
    )}

  </LanguageSwitchContainer>
};
